import { Component, DestroyRef, HostListener, inject, input, viewChild } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { IconSpeaker } from '../../icons/i-speaker';
import { PronunciationService } from '../../services/pronunciation.service';
import { AudioEffectPlayer, AudioPlayer } from '../../utils/audio.utils';
import { RidAudioBubble } from '../rid-audio-bubble/rid-audio-bubble';

export type TAudioSource = string | { pronounce: string };

// Pause between consecutive sources when `src` is a sequence.
const SEQUENCE_PAUSE = 150;

@Component({
  selector: 'rid-audio-button',
  imports: [IconSpeaker, RidAudioBubble],
  templateUrl: './rid-audio-button.html',
  host: {
    class: 'size-fit flex justify-self-center',
  },
})
export class RidAudioButton {
  private readonly $pronunciationService = inject(PronunciationService);
  private readonly destroyRef = inject(DestroyRef);

  /** A single source, or a sequence of sources that are played one after another. */
  src = input.required<TAudioSource | TAudioSource[]>();
  size = input<'XSMALL' | 'SMALL' | 'MEDIUM' | 'LARGE'>('LARGE');

  audioBubble = viewChild.required<RidAudioBubble>('audioBubble');

  @HostListener('pointerenter')
  onHover() {
    AudioEffectPlayer.play('BUTTON_HOVER', { decorative: true });
  }

  /**
   * Plays the source(s) as a single press: a sequence pulses once and plays through as if it were one audio file.
   * `onPart` is called with the index of each source right before it starts playing.
   */
  public async play(onPart?: (index: number) => void) {
    this.audioBubble().play();

    const src = this.src();
    const sources = Array.isArray(src) ? src : [src];

    for (let i = 0; i < sources.length; i++) {
      if (i > 0) await delay(SEQUENCE_PAUSE);
      onPart?.(i);
      await this.playSource(sources[i]);
    }
  }

  private async playSource(src: TAudioSource) {
    if (typeof src === 'string') {
      await AudioPlayer.play(src);
    } else {
      await this.$pronunciationService.tryPronounce(src.pronounce, this.destroyRef);
    }
  }
}
